import {
  FileText,
  ChevronRight,
  Clock,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { HIGHLIGHT_CONFIG, HighlightMaterials } from "./highlights";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ViewMode = "grid" | "list";

interface HighlightCardProps {
  item: HighlightMaterials;
  config: (typeof HIGHLIGHT_CONFIG)[number];
  viewMode: ViewMode;
  onSelect: (item: HighlightMaterials) => void;
  onDelete?: (item: HighlightMaterials) => void;
}

export function daysAgo(date: string | Date) {
  const diff = (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24);
  if (diff < 1) return "Today";
  if (diff < 2) return "1 day ago";
  return `${Math.floor(diff)} days ago`;
}

export function HighlightCard({
  item,
  config,
  viewMode,
  onSelect,
  onDelete,
}: HighlightCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const sources = item.metadata?.document_ids?.length ?? 0;

  const flashcardCount = Array.isArray(item.contents?.flashcards)
    ? item.contents.flashcards.length
    : (item.type === "flashcard" && (item.metadata.count as number)) || null;

  const questionCount = Array.isArray(item.contents?.questions)
    ? item.contents.questions.length
    : (item.type === "quiz" && (item.metadata.count as number)) || null;

  return (
    <>
      <button
        onClick={() => onSelect(item)}
        className={cn(
          "group relative rounded-xl border-2 bg-theme-0 text-left transition-all duration-200",
          "hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]",
          "overflow-hidden",
          config.color.border,
          viewMode === "grid" ? "p-5 min-h-[140px]" : "p-4"
        )}
      >
        {/* Subtle gradient overlay on hover */}
        <div
          className={cn(
            "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          )}
        />

        <div className="relative flex items-start gap-4">
          {/* Enhanced icon badge */}
          <div
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-xl border-2 flex-shrink-0 transition-all duration-200",
              "group-hover:scale-110 group-hover:shadow-theme",
              config.color.bg,
              config.color.border
            )}
          >
            <config.icon className={cn(config.color.text, "h-5 w-5")} />
          </div>

          <div className="min-w-0 flex-1">
            {/* Title with arrow indicator and settings */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <h3 className="font-bold text-theme-0 line-clamp-2 text-sm leading-snug flex-1">
                {item.name || "Untitled highlight"}
              </h3>

              <div className="flex items-center gap-1">
                {/* Settings dropdown */}
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuTrigger
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen((open) => !open);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-theme-1 rounded-md flex-shrink-0"
                  >
                    <MoreVertical className="h-4 w-4 text-theme-1" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-theme-0">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen((open) => !open);
                        setConfirmDeleteOpen(true);
                      }}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <ChevronRight className="h-4 w-4 text-theme-1 group-hover:text-theme-2 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
              </div>
            </div>

            {/* Metadata badges */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {/* Flashcards badge */}
              {flashcardCount !== null && (
                <div
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-medium border",
                    config.color.text === "text-blue-700" &&
                      "bg-blue-50 text-blue-700 border-blue-200",
                    config.color.text === "text-orange-700" &&
                      "bg-orange-50 text-orange-700 border-orange-200",
                    config.color.text === "text-emerald-800" &&
                      "bg-emerald-50 text-emerald-700 border-emerald-200"
                  )}
                >
                  <span>
                    {flashcardCount} card{flashcardCount === 1 ? "" : "s"}
                  </span>
                </div>
              )}

              {/* Quiz questions badge */}
              {questionCount !== null && (
                <div
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-medium border",
                    config.color.text === "text-blue-700" &&
                      "bg-blue-50 text-blue-700 border-blue-200",
                    config.color.text === "text-orange-700" &&
                      "bg-orange-50 text-orange-700 border-orange-200",
                    config.color.text === "text-emerald-800" &&
                      "bg-emerald-50 text-emerald-700 border-emerald-200"
                  )}
                >
                  <span>
                    {questionCount} question{questionCount === 1 ? "" : "s"}
                  </span>
                </div>
              )}

              {/* Sources badge */}
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-theme-2 rounded-lg font-medium text-theme-0 border border-theme">
                <FileText className="h-3 w-3" />
                <span>
                  {sources} source{sources === 1 ? "" : "s"}
                </span>
              </div>

              {viewMode === "list" && (
                <div className="ml-3 flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock className="h-3 w-3" />
                  <span>{daysAgo(item.created_at)}</span>
                </div>
              )}
            </div>

            {/* Time indicator */}
            {viewMode === "grid" && (
              <div className="mt-3 pt-3 border-t border-theme flex items-center gap-1.5 text-xs text-gray-500">
                <Clock className="h-3 w-3" />
                <span>{daysAgo(item.created_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Hover indicator line */}
        <div
          className={cn(
            "absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left",
            config.color.text === "text-blue-700" &&
              "bg-gradient-to-r from-blue-500 to-blue-600",
            config.color.text === "text-orange-700" &&
              "bg-gradient-to-r from-orange-500 to-orange-600",
            config.color.text === "text-emerald-800" &&
              "bg-gradient-to-r from-emerald-500 to-emerald-600"
          )}
        />
      </button>

      {/* Delete confirmation dialog */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent className="bg-theme-0 text-theme-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete highlight?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-semibold">
                {item.name || "this highlight"}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel className="hover:bg-theme-1">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-400 text-black hover:bg-red-400"
              onClick={() => {
                onDelete?.(item);
                setConfirmDeleteOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
